import { marked } from 'marked';

import { Beer } from './types';
import { renderImagePicker, renderThumbs, initThumbs } from './images';
import { imageUrl, deleteBeer, updateBeer } from './api';
import { isAdminMode, closeOverlay, closeOnBackdrop } from './admin';
import { renderRatingInputs, renderRatingView } from './ratings';

export function renderBeerCard(beer: Beer, onUpdate: () => void): HTMLElement {
  const card = document.createElement('div');
  card.className = 'beer-card';
  card.innerHTML = `
    ${beer.images.length ? `<img src="${imageUrl(beer.images[0].id)}" alt="${beer.name}" class="beer-card-image">` : '<div class="beer-card-image beer-card-no-image"></div>'}
    <div class="beer-card-info">
      <h3 class="beer-card-name">${beer.name}</h3>
      <span class="beer-card-abv">${beer.abv}% ABV</span>
    </div>
    <div class="beer-card-admin admin-only">
      <button class="btn-edit" data-id="${beer.id}">Edit</button>
      <button class="btn-delete" data-id="${beer.id}">Delete</button>
    </div>
  `;

  // Click card to show detail overlay
  card.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('btn-edit') || target.classList.contains('btn-delete')) {
      return;
    }
    showBeerOverlay(beer, onUpdate);
  });

  // Delete button
  const deleteBtn = card.querySelector('.btn-delete');
  if (deleteBtn) {
    deleteBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (confirm(`Delete "${beer.name}"?`)) {
        try {
          await deleteBeer(beer.id);
          onUpdate();
        } catch (err) {
          alert('Failed to delete beer');
        }
      }
    });
  }

  // Edit button
  const editBtn = card.querySelector('.btn-edit');
  if (editBtn) {
    editBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      showBeerOverlay(beer, onUpdate, true);
    });
  }

  return card;
}

export function showBeerOverlay(beer: Beer, onUpdate: () => void, editMode = false): void {
  const existing = document.getElementById('beer-overlay');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'beer-overlay';
  overlay.className = 'overlay';

  const admin = isAdminMode();
  const editing = admin && editMode;

  overlay.innerHTML = `
    <div class="overlay-content beer-detail">
      <button class="overlay-close">&times;</button>
      ${editing ? renderEditForm(beer) : renderDetail(beer, admin)}
    </div>
  `;

  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('active'));

  // Close button
  overlay.querySelector('.overlay-close')!.addEventListener('click', () => closeOverlay(overlay));

  // Click backdrop to close
  closeOnBackdrop(overlay);

  // Edit button inside detail view
  const editBtnInDetail = overlay.querySelector('.btn-edit-detail');
  if (editBtnInDetail) {
    editBtnInDetail.addEventListener('click', () => {
      overlay.remove();
      showBeerOverlay(beer, onUpdate, true);
    });
  }

  initThumbs(overlay, beer.images);
  initStock(overlay, beer, onUpdate);

  // Edit form submit
  const form = overlay.querySelector('.beer-edit-form') as HTMLFormElement;
  if (form) {
    const picker = renderImagePicker(
      overlay.querySelector('.image-picker') as HTMLElement,
      beer.images
    );
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(form);
      picker.appendTo(formData);
      try {
        await updateBeer(beer.id, formData);
        overlay.classList.remove('active');
        setTimeout(() => overlay.remove(), 300);
        onUpdate();
      } catch (err) {
        alert('Failed to update the drink');
      }
    });
  }
}

// Descriptions are brewing notes pasted from a phone: single newlines are meant
// as line breaks, not as one run-on paragraph. Raw HTML passes through as it
// always has — the only author is the admin.
function renderDescription(text: string): string {
  return marked.parse(text, { breaks: true, async: false });
}

// Admin-only stock counter: the buttons step it, the field takes a typed value
// on Enter or on leaving it. Every change is saved on its own.
function initStock(root: HTMLElement, beer: Beer, onUpdate: () => void): void {
  const field = root.querySelector('.stock-value') as HTMLInputElement | null;
  if (!field) return;

  const save = async (value: number) => {
    const next = Math.max(0, Math.round(value) || 0);
    if (next === beer.stock) return;
    field.value = String(next);
    const data = new FormData();
    data.append('stock', String(next));
    try {
      await updateBeer(beer.id, data);
      beer.stock = next;
      onUpdate();
    } catch (err) {
      field.value = String(beer.stock); // put the old count back
      alert('Failed to update the stock count');
    }
  };

  root.querySelectorAll('.stock-step').forEach((btn) =>
    btn.addEventListener('click', () =>
      save(beer.stock + +(btn as HTMLElement).dataset.step!)
    )
  );
  field.addEventListener('change', () => save(+field.value));
  field.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      field.blur();
    }
  });
}

function renderDetail(beer: Beer, admin: boolean): string {
  return `
    <div class="beer-detail-layout">
      ${beer.images.length ? `<img src="${imageUrl(beer.images[0].id)}" alt="${beer.name}" class="beer-detail-image">` : ''}
      <div class="beer-detail-info">
        <h2 class="beer-detail-name">${beer.name}</h2>
        <div class="beer-detail-meta">
          ${beer.style ? `<span class="beer-detail-style">${beer.style}</span>` : ''}
          <span class="beer-detail-abv">${beer.abv}% ABV</span>
          ${beer.og ? `<span class="beer-detail-date">OG: ${beer.og}</span>` : ''}
          ${beer.ibu ? `<span class="beer-detail-date">IBU: ${beer.ibu}</span>` : ''}
          <span class="beer-detail-date">Bottled: ${beer.bottling_date}</span>
          ${
            admin
              ? `<span class="stock-control">
                   <button type="button" class="stock-step" data-step="-1">&minus;</button>
                   <input type="number" class="stock-value" min="0" value="${beer.stock}">
                   <button type="button" class="stock-step" data-step="1">+</button>
                   <span class="stock-label">bottles in stock</span>
                 </span>`
              : `<span class="beer-detail-date">${beer.stock} bottles in stock</span>`
          }
        </div>
        ${renderThumbs(beer.images)}
        ${renderRatingView(beer)}
        ${beer.description ? '<h4 class="detail-heading">Description</h4>' : ''}
        <div class="beer-detail-desc">${renderDescription(beer.description)}</div>
        ${admin ? '<button class="btn btn-edit-detail">Edit Drink</button>' : ''}
      </div>
    </div>
  `;
}

function renderEditForm(beer: Beer): string {
  return `
    <h2 class="beer-detail-name">Edit: ${beer.name}</h2>
    <form class="beer-edit-form">
      <div class="form-group">
        <label>Name</label>
        <input type="text" name="name" value="${beer.name}" required>
      </div>
      <div class="form-group">
        <label>Style (optional)</label>
        <input type="text" name="style" value="${beer.style}">
      </div>
      <div class="form-row">
        <div class="form-group">
          <label>ABV (%)</label>
          <input type="number" name="abv" value="${beer.abv}" step="0.1" required>
        </div>
        <div class="form-group">
          <label>OG (opt.)</label>
          <input type="text" name="og" value="${beer.og}">
        </div>
        <div class="form-group">
          <label>IBU (opt.)</label>
          <input type="text" name="ibu" value="${beer.ibu}">
        </div>
      </div>
      <div class="form-group">
        <label>Bottling Date</label>
        <input type="date" name="bottling_date" value="${beer.bottling_date}">
      </div>
      <div class="form-group">
        <label>Description</label>
        <textarea name="description" rows="4">${beer.description}</textarea>
      </div>
      <div class="form-group">
        <label>Tasting</label>
        ${renderRatingInputs(beer.ratings)}
        <textarea name="tasting_notes" rows="3" placeholder="Impressions">${beer.tasting_notes}</textarea>
      </div>
      <div class="form-group">
        <label>Images</label>
        <div class="image-picker"></div>
      </div>
      <button type="submit" class="btn btn-save">Save Changes</button>
    </form>
  `;
}
